import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Calendar, MapPin, Users, Pencil, Trash2 } from "lucide-react";

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    const { data } = await api.get(`/events/${eventId}`);
    setData(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const attending = data?.attending || false;

  const remove = async () => {
    if (!window.confirm("Bu etkinliği ve tüm RSVP'leri silmek istediğine emin misin?")) return;
    try {
      await api.delete(`/events/${eventId}`);
      toast.success("Etkinlik silindi");
      navigate("/events");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Silinemedi");
    }
  };

  const toggle = async () => {
    if (!user) return;
    setBusy(true);
    try {
      await api.post(`/events/${eventId}/rsvp`);
      await load();
      toast.success(attending ? "Katılımın iptal edildi" : "Katılımın kaydedildi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  if (!data)
    return (
      <div className="k-container py-16">
        <div className="k-card h-56 animate-pulse" />
      </div>
    );

  const { event } = data;

  return (
    <div className="k-container py-12 md:py-16">
      <Link to="/events" className="k-btn-ghost text-sm" data-testid="back-to-events">
        <ArrowLeft className="mr-1 h-4 w-4" /> Tüm etkinlikler
      </Link>
      {user?.role === "admin" && (
        <div className="mt-4 flex gap-2">
          <Link to={`/events/${eventId}/duzenle`} className="k-btn-ghost text-sm" data-testid="edit-event-btn">
            <Pencil className="mr-1 h-4 w-4" /> Düzenle
          </Link>
          <button onClick={remove} className="k-btn-ghost text-sm text-destructive" data-testid="delete-event-btn">
            <Trash2 className="mr-1 h-4 w-4" /> Sil
          </button>
        </div>
      )}

      <div className="k-card mt-4 p-8 md:p-10">
        <h1 className="font-display text-5xl text-ink-900 md:text-6xl" data-testid="event-title">
          {event.title}
        </h1>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 font-body text-ink-700">
          <span className="inline-flex items-center gap-2">
            <Calendar className="h-5 w-5 text-sage-700" />
            {new Date(event.start_at).toLocaleString("tr-TR", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
          <span className="inline-flex items-center gap-2">
            <MapPin className="h-5 w-5 text-sage-700" /> {event.location}
          </span>
          <span className="inline-flex items-center gap-2">
            <Users className="h-5 w-5 text-sage-700" /> {event.rsvp_count}
            {event.capacity ? ` / ${event.capacity}` : ""} kişi
          </span>
        </div>

        <p className="mt-8 whitespace-pre-wrap font-body text-lg leading-relaxed text-ink-900" data-testid="event-description">
          {event.description}
        </p>

        <div className="mt-8">
          {user ? (
            <button
              onClick={toggle}
              disabled={busy}
              className={attending ? "k-btn-secondary" : "k-btn-primary"}
              data-testid="rsvp-btn"
            >
              {busy ? "Kaydediliyor…" : attending ? "Katılımı iptal et" : "Katılıyorum"}
            </button>
          ) : (
            <Link to="/login" className="k-btn-primary" data-testid="rsvp-login-cta">
              Katılmak için giriş yap
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
